import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { database } from '../firebaseConfig.js';

/**
 * Save quiz result to user's database
 * @param {string} userId - The user's UID
 * @param {object} resultData - The quiz result data
 * @param {string} testType - 'mock' or 'custom'
 * @param {string} testName - Name of the test/category
 */
export const saveQuizResult = async (userId, resultData, testType = 'custom', testName = 'Quiz') => {
    if (!userId) return;

    try {
        const userRef = doc(database, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) return;

        const userData = userDoc.data();
        const currentStats = userData.quizStats || {
            totalTests: 0,
            totalQuestionsAttempted: 0,
            totalCorrectAnswers: 0
        };

        // Calculate score percentage
        const score = Math.round((resultData.correctAnswers / resultData.totalQuestions) * 100);

        // Create quiz result entry
        const quizResult = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            testType,
            testName: testName || 'Quiz',
            totalQuestions: resultData.totalQuestions,
            correctAnswers: resultData.correctAnswers,
            score,
            timeTaken: resultData.timeTaken,
            completedAt: new Date().toISOString(),
            // Store first 5 questions for preview (recent tests feature)
            questionsPreview: resultData.questionsAndAnswers?.slice(0, 5).map(q => ({
                question: q.question?.substring(0, 100) + (q.question?.length > 100 ? '...' : ''),
                userAnswer: q.user_answer,
                correctAnswer: q.correct_answer,
                isCorrect: q.point === 1
            })) || []
        };

        // Update user stats
        const newStats = {
            totalTests: currentStats.totalTests + 1,
            totalQuestionsAttempted: currentStats.totalQuestionsAttempted + resultData.totalQuestions,
            totalCorrectAnswers: currentStats.totalCorrectAnswers + resultData.correctAnswers
        };

        // Get current quiz history and keep last 50
        const currentHistory = userData.quizHistory || [];
        const updatedHistory = [...currentHistory, quizResult].slice(-50);

        // Calculate overall grade from all tests
        const allScores = updatedHistory.map(q => q.score);
        const overallGrade = calculateOverallGrade(allScores);

        await updateDoc(userRef, {
            quizStats: newStats,
            quizHistory: updatedHistory,
            overallGrade,
            lastQuizCompletedAt: new Date().toISOString()
        });

        console.log('Quiz result saved successfully');
        return true;
    } catch (error) {
        console.error('Error saving quiz result:', error);
        return false;
    }
};

/**
 * Calculate overall grade with lenient algorithm
 * @param {number[]} scores - Array of score percentages
 * @returns {string} Letter grade
 */
export const calculateOverallGrade = (scores) => {
    if (!scores || scores.length === 0) return 'N/A';

    // Weights: more recent tests count more
    const weights = scores.map((_, idx) => 1 + (idx * 0.15));
    const totalWeight = weights.reduce((a, b) => a + b, 0);

    const weightedSum = scores.reduce((acc, score, idx) => {
        return acc + (score * weights[idx]);
    }, 0);

    const averageScore = weightedSum / totalWeight;

    // Lenient curve: bonus for consistent practice
    const consistencyBonus = Math.min(5, scores.length * 0.5);
    const finalScore = Math.min(100, averageScore + consistencyBonus);

    // Convert to letter grade
    if (finalScore >= 90) return 'A+';
    if (finalScore >= 85) return 'A';
    if (finalScore >= 80) return 'A-';
    if (finalScore >= 75) return 'B+';
    if (finalScore >= 70) return 'B';
    if (finalScore >= 65) return 'B-';
    if (finalScore >= 60) return 'C+';
    if (finalScore >= 55) return 'C';
    if (finalScore >= 50) return 'C-';
    if (finalScore >= 45) return 'D+';
    if (finalScore >= 40) return 'D';
    return 'F';
};

/**
 * Get recent tests for preview (last 5)
 * @param {object} userData - User data from Firebase
 * @returns {array} Recent tests with preview data
 */
export const getRecentTests = (userData) => {
    const quizHistory = userData?.quizHistory || [];
    return quizHistory.slice(-5).reverse();
};
